import {
  failureFromResponse,
  type DuplicateContactFlagListDto,
  type ContactMergeRequest,
  type ContactMergeResult,
} from '@signalvote/shared';
import { apiJson, apiVoid } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { refusal } from './useFinanceContributions';
import { useRefreshFinanceAfter } from './useFinanceSettings';

// Duplicate-donor resolution: the data path for the inline ledger resolve panel. A duplicate only
// matters at filing (it understates a donor's totals / limit math), so the panel lets the treasurer
// Merge two split records into one or Dismiss the pair as genuinely different BEFORE filing.

/** Every Open duplicate-contact flag in the campaign. */
const duplicateContactFlagsRead = defineQuery({
  key: () => ['finance', 'duplicate-contact-flags'],
  reads: 'GET /api/finance/duplicate-contact-flags',
  // Keeps a warm close-and-back navigation from re-downloading within 30 s.
  staleTime: 30_000,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<DuplicateContactFlagListDto>('/api/finance/duplicate-contact-flags', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch duplicate-contact flags',
    }),
});

export function useDuplicateContactFlags() {
  return duplicateContactFlagsRead(undefined);
}

/**
 * POST /merge. The endpoint always returns 200 with a discriminated `ContactMergeResult`
 * (`Merged` / `NeedsAmendmentConfirmation` / `Blocked`); only `Merged` writes anything. A merge
 * moves gifts and payments onto the survivor, which report previews and the payments reads show
 * and are not defined reads yet, so it also refreshes the undeclared finance reads. A lost race
 * fails as `stale`; another 409 (the legal-acceptance gate, whose `error` is a machine code)
 * carries its readable `message`.
 */
const mergeContacts = defineWrite({
  send: (data: ContactMergeRequest) =>
    apiJson<ContactMergeResult>('/api/finance/duplicate-contact-flags/merge', {
      method: 'POST',
      json: data,
      error: (body, response) =>
        failureFromResponse(response, body, response.status === 409 ? body.message || body.error || undefined : body.error || 'Failed to merge contacts'),
    }),
});

export function useMergeContacts() {
  return useRefreshFinanceAfter(mergeContacts());
}

/**
 * POST /{id}/dismiss: marks the pair genuinely different (204). Its write set names the flag, which
 * refreshes the flag list, the close hub and the dashboard attention count.
 */
export const useDismissDuplicateFlag = defineWrite({
  send: (flagId: string) =>
    apiVoid(`/api/finance/duplicate-contact-flags/${flagId}/dismiss`, {
      method: 'POST',
      error: refusal('Failed to dismiss duplicate flag'),
    }),
});
